import { TableCell } from '@ovComponents/2-component/table/table';
import SmallDollarPercentage from '@ovComponents/4-module/widgets/portfolio/components/smallDollarPercentage';
import { PORTFOLIO_COLUMN_WIDTH } from '@ovComponents/4-module/widgets/portfolio/components/utils';
import { TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL } from '@ovComponents/4-module/widgets/portfolio/portfolioTestIds';
import { Typography, useTheme } from 'hephaestus/web';
import type { AuditModelDeviationDisplay } from '../../utils/snapshot/resolveAuditModelDeviationDisplay';

export type AuditReportTargetCellProps = {
  expectedValueCents?: number;
  expectedPercentage?: number;
  index: number;
};

/** Portfolio report target $ / % — matches live portfolio widget Target column. */
export const AuditReportTargetCell = ({
  expectedValueCents,
  expectedPercentage,
  index,
}: AuditReportTargetCellProps) => (
  <TableCell
    right
    sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
      index,
      'target',
    )}
  >
    {expectedValueCents == null && expectedPercentage == null ? (
      <Typography variant='bodySmall' color='textSecondary'>
        —
      </Typography>
    ) : (
      <SmallDollarPercentage
        cents={expectedValueCents ?? 0}
        percentage={expectedPercentage ?? 0}
      />
    )}
  </TableCell>
);

export type AuditProposedCellProps = {
  proposedCents?: number;
  index: number;
};

export const AuditProposedCell = ({
  proposedCents,
  index,
}: AuditProposedCellProps) => (
  <TableCell
    right
    sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
      index,
      'proposed',
    )}
  >
    {proposedCents == null ? (
      <Typography variant='bodySmall' color='textSecondary'>
        —
      </Typography>
    ) : (
      <SmallDollarPercentage cents={proposedCents} showPercentage={false} />
    )}
  </TableCell>
);

export type AuditModelDeviationCellProps = {
  display: AuditModelDeviationDisplay;
  index: number;
  borderRight?: boolean;
};

export const AuditModelDeviationCell = ({
  display,
  index,
  borderRight = false,
}: AuditModelDeviationCellProps) => {
  const theme = useTheme();

  const content =
    display.kind === 'empty' ? (
      <Typography variant='bodySmall' color='textSecondary'>
        —
      </Typography>
    ) : (
      <SmallDollarPercentage
        showColor
        cents={display.deviationCents}
        percentage={display.deviationPercentage}
      />
    );

  return (
    <TableCell
      right
      sx={{
        ...(borderRight
          ? { borderRight: `1px solid ${theme.color.outlineVariant}` }
          : {}),
        minWidth: PORTFOLIO_COLUMN_WIDTH.security,
      }}
      data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
        index,
        'deviation',
      )}
    >
      {content}
    </TableCell>
  );
};
